import { useSelector, useDispatch } from "react-redux";
import { setTokens, logout, setUser } from "../redux/auth";
import { getTransport, configureTransport } from "./transport";
import { useNavigate } from "react-router-dom";
import axios from "axios";

let refreshPromise = null;

const getNewToken = (dispatch, navigate) => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    try {
      const rememberMe = localStorage.getItem("rememberMe") === "true";
      const { defaults } = getTransport();

      const res = await axios.post(
        `${defaults.baseURL}/api/refresh`,
        { rememberMe },
        { withCredentials: true },
      );

      const newToken = res.data.data.tokens.tokens.accessToken;

      configureTransport(newToken);
      dispatch(setTokens({ accessToken: newToken }));
      dispatch(setUser(res.data.data.user));

      return newToken;
    } catch {
      localStorage.removeItem("rememberMe");
      dispatch(logout());
      navigate("/login");
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const generateHook = (callback) => () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  return callback(async (e) => {
    if (!e.response || e.response.status !== 401) throw e;

    const config = e.response.config;
    const token = await getNewToken(dispatch, navigate);
    if (!token) throw e;

    if (config.headers && config.headers.Authorization) {
      delete config.headers.Authorization;
    }
    return getTransport()
      .request(config)
      .then((r) => r.data);
  });
};

export const usePost = generateHook(
  (middleware) => (path, payload, config) =>
    getTransport()
      .post(`/api/${path}`, payload, config)
      .then((response) => response.data)
      .catch(middleware),
);

export const useGet = generateHook(
  (middleware) => (path) =>
    getTransport()
      .get(`/api/${path}`)
      .then((response) => response.data)
      .catch(middleware),
);

export const usePut = generateHook(
  (middleware) => (path, payload, config) =>
    getTransport()
      .put(`/api/${path}`, payload, config)
      .then((response) => response.data)
      .catch(middleware),
);

export const usePatch = generateHook(
  (middleware) => (path, payload, config) =>
    getTransport()
      .patch(`/api/${path}`, payload, config)
      .then((response) => response.data)
      .catch(middleware),
);

export const useDelete = generateHook(
  (middleware) => (path, config) =>
    getTransport()
      .delete(`/api/${path}`, config)
      .then((response) => response.data)
      .catch(middleware),
);

export const get = (path, config) =>
  getTransport()
    .get(`/api/${path}`, config)
    .then((response) => response.data);

export const post = async (path, payload, config) =>
  getTransport()
    .post(`/api/${path}`, payload, config)
    .then((response) => response.data);

export const put = (path, payload = {}) =>
  getTransport()
    .put(`/api/${path}`, payload)
    .then((response) => response.data);

export const patch = (path, payload = {}) =>
  getTransport()
    .patch(`/api/${path}`, payload)
    .then((response) => response.data);

export const httpDelete = (path, config) =>
  getTransport()
    .delete(`/api/${path}`, config)
    .then((response) => response.data);
